
import React from 'react';
import { useLanguage } from '../lib/LanguageContext';
import { 
  Phone, 
  Mail, 
  ArrowRight, 
  Truck, 
  Wrench, 
  Settings, 
  Package, 
  User,
  Tractor,
  Construction,
  Sprout,
  ClipboardCheck,
  Droplets,
  Hammer,
  Snowflake,
  Cpu
} from 'lucide-react';
import { Link } from 'react-router-dom';

const iconMap = {
  Tractor,
  Construction,
  Sprout,
  ClipboardCheck,
  Droplets,
  Hammer,
  Snowflake,
  Cpu
};

export default function Home() {
  const { t, lang } = useLanguage();

  const services = [
    { key: 'onSite', icon: Truck, path: '/udekoerende-service' },
    { key: 'metalwork', icon: Wrench, path: '/smede-og-maskinforretning' },
    { key: 'crane', icon: Settings, path: '/kran-og-lift' },
    { key: 'parts', icon: Package, path: '/reservedele' },
  ];

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/crane-service.jpg"
            alt="Maskinforretning"
            className="w-full h-full object-cover brightness-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 to-transparent" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-6 border-l-4 border-[#f59c01] pl-4 inline-block">
              {t.hero.title}
            </h1>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl leading-relaxed">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${t.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center bg-[#f59c01] text-white px-4 py-2.5 md:px-8 md:py-4 rounded-lg font-bold text-sm md:text-lg hover:bg-[#e08e01] transition-all shadow-lg hover:shadow-[#f59c01]/25"
              >
                <Phone className="mr-2 h-4 w-4 md:h-5 md:w-5" />
                {t.hero.cta_call}
              </a>
              <a
                href={`mailto:${t.email}`}
                className="inline-flex items-center justify-center bg-slate-800 text-white px-4 py-2.5 md:px-8 md:py-4 rounded-lg font-bold text-sm md:text-lg hover:bg-slate-700 transition-all border border-slate-700"
              >
                <Mail className="mr-2 h-4 w-4 md:h-5 md:w-5" />
                {t.hero.cta_email}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-12 md:py-16 lg:py-24 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 border-l-4 border-[#f59c01] pl-4 inline-block">
              {t.ui.ourServices}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t.ui.servicesSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-8">
            {services.map((service) => {
              const data = t.sections[service.key];
              const Icon = service.icon;
              return (
                <Link
                  key={service.key}
                  to={service.path}
                  className="group bg-white p-8 rounded-xl shadow-sm border border-slate-200 hover:shadow-xl hover:border-[#f59c01]/20 transition-all flex flex-col h-full"
                >
                  <div className="w-12 h-12 bg-orange-50 text-[#f59c01] rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#f59c01] group-hover:text-white transition-colors">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-[#f59c01] transition-colors">
                    {data.title}
                  </h3>
                  <p className="text-slate-600 mb-6 flex-grow">
                    {data.description}
                  </p>
                  <div className="flex items-center text-[#f59c01] font-semibold text-sm">
                    {t.ui.readMore}
                    <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Specialties Grid */}
      <section className="py-12 md:py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 border-l-4 border-[#f59c01] pl-4 inline-block">
              {t.ui.weOffer}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {lang === 'da' 
                ? 'Vi løser en bred vifte af opgaver på vores værksted og med vores udekørende service.' 
                : 'We solve a wide range of tasks in our workshop and with our on-site service.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.specialties && t.specialties.map((spec) => {
              const IconComponent = iconMap[spec.icon] || Wrench;
              return (
                <div 
                  key={spec.id} 
                  className="bg-slate-50 p-6 rounded-xl border border-slate-100 hover:border-[#f59c01]/30 hover:bg-white hover:shadow-lg transition-all"
                >
                  <div className="w-12 h-12 bg-orange-50 text-[#f59c01] rounded-lg flex items-center justify-center mb-4">
                    <IconComponent className="h-6 w-6" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">
                    {spec.title}
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {spec.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured: On-Site Service */}
      <section className="py-12 md:py-16 lg:py-24 bg-slate-950 text-white overflow-hidden relative">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_20%_30%,rgba(245,156,1,0.15)_0%,transparent_50%)]" />
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_80%_70%,rgba(245,156,1,0.1)_0%,transparent_40%)]" />
          <div className="absolute inset-0 opacity-20 [mask-image:radial-gradient(ellipse_at_center,black,transparent_80%)] bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:40px_40px]" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
              <h2 className="text-3xl md:text-5xl font-bold mb-6 border-l-4 border-[#f59c01] pl-4 inline-block">{t.sections.onSite.title}</h2>
              <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                {t.sections.onSite.description}
              </p>
              <ul className="space-y-4 mb-10">
                {t.sections.onSite.features.map((feature, i) => (
                  <li key={i} className="flex items-center text-slate-300">
                    <div className="w-5 h-5 rounded-full bg-[#f59c01] flex items-center justify-center mr-3 flex-shrink-0">
                      <div className="w-2 h-2 bg-white rounded-full" />
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>
              <Link
                to="/udekoerende-service"
                className="inline-flex items-center bg-[#f59c01] text-white px-4 py-2 md:px-8 md:py-3 rounded-lg font-bold hover:bg-[#e08e01] transition-colors text-xs md:text-base"
              >
                {t.ui.readMore}
              </Link>
            </div>
            <div className="lg:w-1/2 relative">
              <div className="absolute -inset-4 bg-[#f59c01]/20 blur-3xl rounded-full" />
              <img
                src="/images/mobile_repair_image.png"
                alt={t.sections.onSite.title}
                className="relative rounded-2xl shadow-2xl border border-slate-700 w-full object-cover object-right h-[300px] md:h-[350px] lg:h-[450px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Presence */}
      <section className="py-12 md:py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row-reverse items-center gap-16">
            <div className="md:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 border-l-4 border-[#f59c01] pl-4 inline-block">{t.sections.about.title}</h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed italic">
                "{t.sections.about.content}"
              </p>
              <div className="flex items-center">
                <div className="mr-4">
                  <p className="font-bold text-slate-900">Karsten Andersen</p>
                  <p className="text-sm text-slate-500">{t.ui.owner}</p>
                </div>
              </div>
            </div>
            <div className="md:w-1/2">
              <img
                src={t.sections.about.image}
                alt="Karsten Andersen"
                className="rounded-2xl shadow-xl w-full max-w-md mx-auto aspect-square object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Band - Removed as it is now handled globally in App.jsx */}
    </div>
  );
}
